import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pin, Archive, ArchiveRestore, RotateCcw, Pause, Play, CheckCheck } from "lucide-react";
import { TaskHeader } from "./TaskHeader";
import { HoldForm, ReopenForm, ArchiveForm, BlockForm } from "./TaskActionForms";
import { TaskChecklist, TaskSubtasks, TaskAttachments, TaskShare, TaskComments } from "./TaskCollab";

const NEXT_ACTION = { not_started: "start", in_progress: "complete", blocked: "resume" };

export function OfficeTaskDetail({ taskId, onClose, onChanged }) {
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [blockForm, setBlockForm] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [comment, setComment] = useState("");
  const [directory, setDirectory] = useState([]);
  const [shareUser, setShareUser] = useState("");
  const shareCandidates = useMemo(() => directory.filter((u) => u.user_id !== t?.assigned_to?.user_id && !(t?.shared_with || []).some((sw) => sw.user_id === u.user_id)), [directory, t]);
  const [holdForm, setHoldForm] = useState(null);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveForm, setArchiveForm] = useState({ reason: "test", remarks: "" });
  const [subtaskTitle, setSubtaskTitle] = useState("");


  const load = useCallback(async () => {
    if (!taskId) return;
    const r = await api.get(`/office/tasks/${taskId}`);
    setT(r.data);
    const d = await api.get("/office/directory").catch(() => ({ data: [] }));
    setDirectory(d.data);
  }, [taskId]);
  useEffect(() => { setT(null); setBlockForm(null); setRemarks(""); load(); }, [taskId, load]);

  const act = async (action, body = {}) => {
    try {
      await api.post(`/office/tasks/${taskId}/${action}`, body);
      toast.success(`Task ${action} recorded`);
      setBlockForm(null);
      setRemarks("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed");
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await act("comments", { text: comment });
    setComment("");
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/office/tasks/${taskId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded");
      await load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed");
    }
    e.target.value = "";
  };

  const share = async () => {
    if (!shareUser) return;
    try {
      await api.post(`/office/tasks/${taskId}/share`, { user_ids: [shareUser] });
      toast.success("Access granted");
      setShareUser("");
      await load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Share failed");
    }
  };

  const unshare = async (uid) => {
    try {
      await api.post(`/office/tasks/${taskId}/unshare`, { user_id: uid });
      toast.success("Access removed");
      await load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const addSubtask = async () => {
    if (!subtaskTitle.trim()) return;
    try {
      await api.post("/office/tasks", {
        title: subtaskTitle, department: t.department, assigned_to: t.assigned_to.user_id,
        reporting_manager: t.reporting_manager?.user_id || null, parent_task_id: t.task_id,
        due_date: t.due_date, task_type: "project",
      });
      toast.success("Subtask created");
      setSubtaskTitle("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const restoreTask = async () => {
    try {
      const r = await api.post(`/office/tasks/${taskId}/restore`, { reason: "Restored by admin" });
      toast.success(r.data.restore_warnings?.length ? `Restored — warnings: ${r.data.restore_warnings.join(", ")}` : "Task restored");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const isAssignee = t?.assigned_to?.user_id === user?.user_id || user?.role === "admin";
  const isManager = t && (user?.role === "admin" || t.reporting_manager?.user_id === user?.user_id);
  const canShare = t && (isManager || t.created_by?.user_id === user?.user_id);

  const canTick = (isAssignee || isManager || (t?.shared_with || []).some((sw) => sw.user_id === user?.user_id)) && !t?.archived;
  const togglePin = async () => { await api.post(`/office/tasks/${taskId}/${t.pinned ? "unpin" : "pin"}`); await load(); onChanged?.(); };

  return (
    <Dialog open={!!taskId} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="office-task-detail">
        {!t ? (
          <>
            <DialogHeader><DialogTitle className="sr-only">Loading task</DialogTitle><DialogDescription className="sr-only">Loading office task detail</DialogDescription></DialogHeader>
            <p className="text-sm text-muted-foreground">Loading…</p>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase">{t.title}</DialogTitle>
              <DialogDescription className="sr-only">Office task detail and actions</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <TaskHeader t={t} />

              <div className="flex flex-wrap gap-2">
                {t.status === "not_started" && isAssignee && (
                  <Button size="sm" className="bg-primary text-primary-foreground" onClick={() => act("start")} data-testid="otd-start">Start</Button>)}
                {t.status === "in_progress" && isAssignee && (
                  <Button size="sm" variant="outline" onClick={() => setBlockForm({ reason: "", waiting_on: "", expected_resolution: "" })} data-testid="otd-block">Mark Blocked</Button>)}
                {t.status === "blocked" && isAssignee && (
                  <Button size="sm" className="bg-primary text-primary-foreground" onClick={() => act("resume")} data-testid="otd-resume">Resume</Button>)}
                {t.status === "in_progress" && isAssignee && (
                  <Button size="sm" className="bg-signal-green hover:bg-signal-green/90 text-white" onClick={() => act("complete", { remarks: remarks || null })} data-testid="otd-complete">Complete</Button>)}
                {t.status === "rework_required" && isAssignee && (
                  <Button size="sm" className="bg-primary text-primary-foreground" onClick={() => act("start")} data-testid="otd-rework-start">Start Rework</Button>)}
                {t.status === "completed" && isManager && (
                  <Button size="sm" className="bg-signal-green hover:bg-signal-green/90 text-white" onClick={() => act("verify", { remarks: remarks || null })} data-testid="otd-verify">Approve</Button>)}
                {t.status === "completed" && isManager && (
                  <Button size="sm" variant="outline" className="text-signal-red" onClick={() => { const r = window.prompt("Reason for returning work (required):"); if (r) act("reject", { reason: r }); }} data-testid="otd-reject">Return for Rework</Button>)}
                {!["verified", "closed", "cancelled"].includes(t.status) && (isManager || t.created_by?.user_id === user?.user_id) && !t.archived && (
                  <Button size="sm" variant="ghost" className="text-signal-red" onClick={() => { const r = window.prompt("Cancel reason (required):"); if (r) act("cancel", { reason: r }); }} data-testid="otd-cancel">Cancel</Button>)}
                {["in_progress", "rework_required"].includes(t.status) && isAssignee && (
                  <Button size="sm" variant="outline" onClick={() => { const r = window.prompt("Completion evidence (link / description):"); if (r) act("evidence", { text: r }); }} data-testid="otd-evidence">+ Evidence</Button>)}
                {["completed", "verified"].includes(t.status) && isManager && (
                  <Button size="sm" variant="outline" onClick={() => act("close", {})} data-testid="otd-close">Close</Button>)}
                {t.ack_required && !t.acknowledged && t.assigned_to?.user_id === user?.user_id && !t.archived && (
                  <Button size="sm" className="bg-primary text-primary-foreground" onClick={() => act("acknowledge")} data-testid="otd-ack"><CheckCheck className="w-3.5 h-3.5 mr-1" />Acknowledge</Button>)}
                {["not_started", "in_progress", "blocked"].includes(t.status) && (isAssignee || isManager) && !t.archived && (
                  <Button size="sm" variant="outline" onClick={() => setHoldForm({ reason: "", expected_resume_date: "" })} data-testid="otd-hold"><Pause className="w-3.5 h-3.5 mr-1" />On Hold</Button>)}
                {t.status === "on_hold" && (isAssignee || isManager) && (
                  <Button size="sm" className="bg-primary text-primary-foreground" onClick={() => act("resume-hold")} data-testid="otd-resume-hold"><Play className="w-3.5 h-3.5 mr-1" />Resume</Button>)}
                {["completed", "verified", "closed"].includes(t.status) && (isManager || isAssignee || t.created_by?.user_id === user?.user_id) && !t.archived && (
                  <Button size="sm" variant="outline" onClick={() => { setReopenOpen(true); setReopenReason(""); }} data-testid="otd-reopen"><RotateCcw className="w-3.5 h-3.5 mr-1" />Reopen</Button>)}
                <Button size="sm" variant="ghost" onClick={togglePin} data-testid="otd-pin">
                  <Pin className={`w-3.5 h-3.5 mr-1 ${t.pinned ? "fill-primary text-primary" : ""}`} />{t.pinned ? "Pinned" : "Pin"}</Button>
                {user?.role === "admin" && !t.archived && (
                  <Button size="sm" variant="ghost" className="text-signal-red" onClick={() => setArchiveOpen(true)} data-testid="otd-archive"><Archive className="w-3.5 h-3.5 mr-1" />Archive</Button>)}
                {user?.role === "admin" && t.archived && (
                  <Button size="sm" className="bg-primary text-primary-foreground" onClick={restoreTask} data-testid="otd-restore"><ArchiveRestore className="w-3.5 h-3.5 mr-1" />Restore</Button>)}
              </div>

              {holdForm && <HoldForm form={holdForm} setForm={setHoldForm} onSubmit={(b) => { act("hold", b); setHoldForm(null); }} />}
              {reopenOpen && <ReopenForm reason={reopenReason} setReason={setReopenReason} onSubmit={(b) => { act("reopen", b); setReopenOpen(false); }} />}
              {archiveOpen && <ArchiveForm form={archiveForm} setForm={setArchiveForm} onSubmit={(b) => { act("archive", b); setArchiveOpen(false); }} />}
              {["in_progress", "completed"].includes(t.status) && (isAssignee || isManager) && (
                <div><Label className="text-xs">Remarks (completion / verification)</Label>
                  <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="otd-remarks" /></div>)}
              {blockForm && <BlockForm form={blockForm} setForm={setBlockForm} onSubmit={(b) => act("block", b)} />}

              <TaskChecklist t={t} canToggle={canTick} onToggle={(k) => act(`checklist/${k}/toggle`)} />
              <TaskSubtasks t={t} canAdd={(isAssignee || isManager) && !t.archived} title={subtaskTitle} setTitle={setSubtaskTitle} onAdd={addSubtask} />
              <TaskAttachments t={t} onUpload={upload} />
              {canShare && <TaskShare t={t} candidates={shareCandidates} shareUser={shareUser} setShareUser={setShareUser} onShare={share} onUnshare={unshare} />}
              <TaskComments t={t} comment={comment} setComment={setComment} onPost={addComment} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
